/**
 * RWBR — dice.
 *
 * Three things matter here.
 *
 * 1. SEEDED (D-005). One stored seed per race. Not for bit-exact replay — we
 *    explicitly are not building that — but so a strange result can be
 *    investigated rather than shrugged at, and so tests are deterministic.
 *
 * 2. THE NOTATIONS DO NOT MIX. "Roll two dice" means a 2-12 SUM on the
 *    FEUD/FUME, DUEL and REPAIR tables, but an 11-66 TWO-DIGIT READ on
 *    PROBLEM FINDER, RARE RESULTS and every Race Week chart. Conflating them
 *    has already caused one bug in this project's source material. They are
 *    separate functions and there is no generic "roll 2d6" helper on purpose.
 *
 * 3. D-018 — THE DICE CAN BE FED. This is a tabletop game and Howard rolls
 *    real dice at a real table; the engine's job in that mode is to read the
 *    faces off the felt, not to make them up. The handoff is explicit about
 *    what that has to mean:
 *
 *      "Manual dice entry (D-018) — do it properly or not at all; it means
 *       `Dice` becomes a source you can feed, every roll gets marked as
 *       hand-entered in the ledger, and the turn loop has to pause mid-turn.
 *       Not a text box."
 *
 *    So this file owns the first two thirds of that: a face QUEUE, and a
 *    STREAM LOG that records, for every single face the engine ever took,
 *    whether a human supplied it or the PRNG made it. `manualRace.ts` owns
 *    the pause.
 *
 * ---------------------------------------------------------------------------
 * WHY THE STREAM LOG IS THE PROVENANCE RECORD, AND NOT A FLAG ON `DiceRecord`
 * ---------------------------------------------------------------------------
 *
 * The obvious place to mark a hand-entered roll is `DiceRecord` in
 * `ruleEvent.ts` — that is the ledger row a reader actually looks at. It is
 * marked there too (see `DiceRecord.entry`), but it CANNOT be the primary
 * record, and that was settled by measurement rather than by taste.
 *
 * `DiceRecord`s are assembled by the ~30 rule modules out of faces they were
 * handed earlier, often several rolls earlier — `feudFume.ts` rolls the
 * hot-head die, then the fume-target die, then a 2d6 sum, and only THEN builds
 * all three records. So the order the ledger records faces in is not the order
 * the engine drew them, and some draws are never recorded at all (the weighted
 * pick inside `ChartBTracker.take`, for one). Measured over five 50-turn races:
 *
 *     seed 424242 — 837 faces drawn, 762 recorded, 271 attributable in order
 *     seed      7 — 1065 drawn, 992 recorded, 286 attributable
 *     seed  31337 —  867 drawn, 801 recorded, 337 attributable
 *
 * Roughly a third. Reconstructing "which recorded 6 was Howard's" after the
 * fact is therefore not a thing that can be done honestly, and a mark that is
 * right a third of the time is worse than no mark — it is the "hand-entered 6
 * that looks identical to a rolled 6" the requirement names, wearing a badge.
 *
 * The stream log has none of that problem because it is written AT THE DRAW,
 * by the only object that knows where the face came from. It is complete (it
 * carries the ~8% of draws the rule modules never record), it is ordered, and
 * it is exact. It is what gets saved with a hand-rolled race.
 *
 * `DiceRecord.entry` is then stamped from it in `runTurn` over a whole turn's
 * window, which IS exact — see the note there.
 */

/** mulberry32 — small, fast, well-distributed, and trivially reproducible. */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------------------
// Provenance
// ---------------------------------------------------------------------------

/**
 * Where one face came from.
 *
 * `hand` means a human read it off a physical die and typed it in. `engine`
 * means the seeded PRNG produced it. There is no third option and there is no
 * default — every draw is one or the other, decided at the moment it happens.
 */
export type DiceEntry = 'engine' | 'hand';

/**
 * The READING a die was drawn as part of, which is the only thing the dice
 * object can honestly say about a face's meaning.
 *
 * This is deliberately a superset of `ruleEvent.ts`'s `DiceNotation`: that type
 * describes how a rule module READ a finished pair, this one describes the call
 * the engine made. `race-cam` and `challenge` are their own entries because
 * they are three-die throws whose dice have named, non-interchangeable roles.
 */
export type DrawNotation = 'd6' | 'd10' | 'dN' | '2d6-sum' | '2d10-sum' | '11-66' | 'race-cam' | 'challenge';

/** One face, exactly as it was produced, with where it came from. */
export interface DieDraw {
  /** 0-based position in this race's stream. The stream is append-only. */
  index: number;
  /** How many faces the die had. 6 for almost everything. */
  sides: number;
  /** What it came up. Always 1..sides. */
  face: number;
  entry: DiceEntry;
  notation: DrawNotation;
  /**
   * Which die of the throw this was, when the throw has named dice:
   * `red`/`white`/`blue` on a challenge, `black`/`metallic` on the RACE CAM,
   * `first`/`second` on a 2d6 sum. Undefined for a lone die.
   */
  role?: string;
  /**
   * The phase of the turn that was running when this die was needed — the last
   * thing `runTurn` announced before it asked (see `Dice.phase`).
   *
   * HONEST SCOPE. This says which PART OF THE TURN the die belongs to, not
   * which driver it is about. The dice object is at the bottom of a call stack
   * about eight frames deep and the driver is known only at the top; carrying
   * it down would mean threading a context argument through every one of the
   * thirty rule modules. What the die is FOR, per driver, is in the ledger
   * event the roll produced — this is the coarse, always-available answer.
   */
  phase?: string;
}

/** Presentation receipt, emitted at the draw separately from hand-entered provenance. */
export interface ObservedDraw extends DieDraw {
  throwIndex: number;
  /**
   * WHO THIS THROW IS FOR — presentation only (Howard, 7 Oct 2026: "use the
   * actual driver cards when they are called"). Set by a rule module around a
   * roll it makes for a known driver (a Fast Pit check, a draft, an ESCAPE
   * check), or to the eligible pool before a selection. Nothing in the engine
   * reads it; it is copied onto the presentation receipt and no further.
   */
  subject?: readonly string[];
  /** What a uniform selection (`dN`) is choosing among, in words the table can print. */
  pick?: DrawPick;
}

/** The kinds of uniform selection the rules make. */
export type DrawPick = 'trouble-space' | 'hot-head' | 'fume-target';

/**
 * What the engine is waiting for, when the queue runs dry and the policy is to
 * stop. This is the object the dice-entry screen renders.
 */
export interface DiceRequest {
  /** 0-based position in the stream — i.e. how many faces have been given. */
  index: number;
  sides: number;
  notation: DrawNotation;
  role?: string;
  phase?: string;
  /**
   * The faces already given for THIS throw. Asking for the blue die of a
   * challenge is a different question when red and white are already on the
   * table, and the screen should be able to show them.
   */
  given: readonly number[];
  /** The roles of every die in this throw, in order. */
  roles: readonly string[];
}

/**
 * Thrown by `Dice.d` when the fed queue is empty and the exhaustion policy is
 * `suspend`. NOT an error condition — it is the pause.
 *
 * It extends `Error` so that it unwinds the whole call stack the way a throw
 * does, which is the entire mechanism: `runTurn` is eight frames deep inside
 * `runRace` when a die is needed, and this is how the engine gets back out to
 * the player without every frame in between having to know about dice entry.
 * `manualRace.ts` catches it. Nothing else should.
 */
export class DiceNeeded extends Error {
  readonly request: DiceRequest;
  /** Every face drawn before the engine stopped, in order. */
  readonly stream: readonly DieDraw[];

  constructor(request: DiceRequest, stream: readonly DieDraw[]) {
    super(
      `the engine needs a ${request.sides}-sided die` +
        (request.role ? ` (${request.role})` : '') +
        ` for the ${request.notation} throw and the fed queue is empty ` +
        `(${request.index} face${request.index === 1 ? '' : 's'} given so far)`,
    );
    this.name = 'DiceNeeded';
    this.request = request;
    this.stream = stream;
  }
}

/**
 * What to do when the fed queue runs out.
 *
 *   `suspend` — stop, and throw `DiceNeeded`. THE DEFAULT WHENEVER A FEED IS
 *               SUPPLIED AT ALL, and the answer to the requirement's "falling
 *               back to the PRNG when the queue is empty (or refusing — you
 *               decide and justify it)". It refuses, for two reasons:
 *
 *               1. A silent fall-back would make the pause a lie. The engine
 *                  would sail past the die it was supposed to stop at and
 *                  finish the race on numbers nobody rolled, and the player's
 *                  next entry would land somewhere else entirely.
 *               2. It would make the ledger lie. Half a race of `engine` draws
 *                  inside what the player believes was a hand-rolled race is
 *                  precisely the corruption the marking requirement exists to
 *                  prevent.
 *
 *   `prng`    — take the rest from the seeded generator. This exists so that
 *               "I rolled the first ten turns by hand, now just run it" is
 *               possible ON PURPOSE, with the crossover recorded face by face
 *               in the stream. It is never the default.
 *
 * WHAT `prng` DOES AND DOES NOT PICK UP. The generator is NOT turned while the
 * hand queue is serving, so the engine's first face after the crossover is the
 * FIRST face of the seed's sequence, not the two-hundredth. That is deliberate:
 * burning generator draws to keep an alignment with a race that was never run
 * would be machinery in service of a coincidence. The rule is the simple one —
 * hand-fed faces come from the table, engine faces come from the seed, and
 * neither touches the other. The crossover race is still fully deterministic in
 * (seed, faces), which is the property anything downstream actually needs.
 */
export type ExhaustionPolicy = 'suspend' | 'prng';

/** A queue of faces to draw from, and what to do when it runs out. */
export interface DiceFeed {
  /** Faces in the order they were rolled at the table. May be empty. */
  faces: readonly number[];
  /** Defaults to `suspend`. See `ExhaustionPolicy`. */
  exhausted?: ExhaustionPolicy;
}

// ---------------------------------------------------------------------------
// The dice
// ---------------------------------------------------------------------------

/** The throw currently being assembled, so each face knows its own role. */
interface PendingThrow {
  notation: DrawNotation;
  roles: readonly string[];
  /** How many faces of this throw have been drawn. */
  i: number;
  /** Those faces, for the request. */
  faces: number[];
  throwIndex: number;
}

export class Dice {
  private rng: () => number;
  /** How many individual die faces have been produced. Useful in tests. */
  public rolls = 0;

  /**
   * The phase of the turn currently running, as announced by `runTurn`.
   *
   * Written by the turn loop at each phase boundary, read only when a die has
   * to be asked for. It is a plain field rather than a scope with a callback
   * because a stale label is a far smaller sin than a mis-nested try/finally in
   * a 1,200-line function — and because it is only ever displayed, never used
   * to decide anything. See `DieDraw.phase` for what it does and does not
   * claim.
   */
  public phase: string | undefined;

  /**
   * Presentation only — see `ObservedDraw.subject` / `ObservedDraw.pick`. Like
   * `phase`, written by the rule that is about to roll and never read to
   * decide anything. Use `about()` so it cannot outlive the roll.
   */
  public subject: readonly string[] | undefined;
  public pick: DrawPick | undefined;

  /**
   * Run `fn` with the throws inside it marked as being about `ids` (and, for a
   * selection, what is being chosen). Restores the previous marks afterwards,
   * including when a hand-rolled race suspends for a die mid-roll.
   */
  about<T>(ids: readonly string[] | undefined, fn: () => T, pick?: DrawPick): T {
    const subject = this.subject;
    const drawPick = this.pick;
    this.subject = ids && ids.length ? ids : undefined;
    this.pick = pick;
    try {
      return fn();
    } finally {
      this.subject = subject;
      this.pick = drawPick;
    }
  }

  /**
   * Hand-fed faces, in table order. Only present when a feed was supplied.
   * Shifted from the front as the engine draws.
   */
  private handQueue: number[] = [];
  private policy: ExhaustionPolicy = 'prng';
  /**
   * True when this object was built with a feed — i.e. when this race is a
   * hand-rolled one. When false, `stream` is left empty and every code path
   * added for D-018 is inert, which is how the determinism-from-seed guarantee
   * survives untouched.
   */
  private fed = false;

  /** Every face drawn, in order, with its provenance. Empty unless `fed`. */
  private readonly draws: DieDraw[] = [];

  private pending: PendingThrow | null = null;

  private nextThrow = 0;

  constructor(seed: number, feed?: DiceFeed, private readonly observe?: (draw: ObservedDraw) => void) {
    this.rng = makeRng(seed);
    if (feed) {
      this.fed = true;
      this.policy = feed.exhausted ?? 'suspend';
      this.handQueue = feed.faces.map((f, i) => {
        if (!Number.isInteger(f) || f < 1) {
          throw new RangeError(`fed face #${i + 1} is ${f}; a die face is a whole number of 1 or more`);
        }
        return f;
      });
    }
  }

  /**
   * The provenance log: every face this object produced, in order.
   *
   * EMPTY IN SEEDED MODE, on purpose. In a seeded race the seed IS the stream —
   * D-005 already guarantees the faces are reproducible from one stored number,
   * so writing eight hundred objects per race would be recording something the
   * record already contains. In a hand-rolled race the faces came off a table
   * and exist nowhere else, so they are kept.
   */
  get stream(): readonly DieDraw[] {
    return this.draws;
  }

  /** True when this dice object is drawing from a hand-fed queue. */
  get isFed(): boolean {
    return this.fed;
  }

  /** How many fed faces are left unspent. Should be 0 at the end of a race. */
  get queued(): number {
    return this.handQueue.length;
  }

  /**
   * How the faces in `[from, to)` of the stream were entered.
   *
   * `null` when the window is empty or this is a seeded race (nothing to say).
   * `mixed` when the window straddles the crossover from hand to PRNG — which
   * is reported rather than rounded off, because a window that is half one and
   * half the other is a THIRD fact and calling it either of the first two would
   * be inventing a result.
   */
  entryOver(from: number, to: number): DiceEntry | 'mixed' | null {
    if (!this.fed) return null;
    const slice = this.draws.slice(Math.max(0, from), Math.max(0, to));
    if (slice.length === 0) return null;
    const first = slice[0].entry;
    return slice.every((d) => d.entry === first) ? first : 'mixed';
  }

  /**
   * One die of `sides` faces.
   *
   * THE ONE FUNNEL. Every face in the entire engine comes through here — that
   * was already true before D-018 (it is why `ScriptedDice` in the test helper
   * only has to override this one method), and it is the fact that makes a
   * feedable dice source a small change rather than a rewrite.
   */
  d(sides: number): number {
    // Seeded mode: byte-for-byte what this method did before D-018. Nothing
    // below the first branch runs, so `runRace` with a given seed and no feed
    // produces exactly the race it produced yesterday.
    if (!this.fed && !this.observe) {
      this.rolls++;
      return Math.floor(this.rng() * sides) + 1;
    }

    const solo = this.pending === null;
    if (solo) this.beginThrow(sides === 10 ? 'd10' : sides === 6 ? 'd6' : 'dN', []);
    const p = this.pending as PendingThrow;

    let face: number;
    let entry: DiceEntry;
    if (this.handQueue.length > 0) {
      face = this.handQueue.shift() as number;
      // Never accept a face the die cannot show. A 7 on a d6 is not a typo to
      // be shrugged at — it means the queue and the engine have drifted out of
      // step, and every face after it would be attributed to the wrong roll.
      if (face > sides) {
        throw new RangeError(
          `hand-entered face #${this.draws.length + 1} is ${face}, but the engine asked for a ` +
            `${sides}-sided die (${p.notation}${p.roles[p.i] ? `, ${p.roles[p.i]}` : ''})`,
        );
      }
      entry = 'hand';
    } else if (this.policy === 'suspend') {
      // THE PAUSE. See DiceNeeded.
      throw new DiceNeeded(
        {
          index: this.draws.length,
          sides,
          notation: p.notation,
          role: p.roles[p.i],
          phase: this.phase,
          given: [...p.faces],
          roles: [...p.roles],
        },
        this.draws,
      );
    } else {
      face = Math.floor(this.rng() * sides) + 1;
      entry = 'engine';
    }

    this.rolls++;
    const draw: DieDraw = {
      index: this.rolls - 1,
      sides,
      face,
      entry,
      notation: p.notation,
      ...(p.roles[p.i] ? { role: p.roles[p.i] } : {}),
      ...(this.phase ? { phase: this.phase } : {}),
    };
    if (this.fed) this.draws.push(draw);
    this.observe?.({
      ...draw,
      throwIndex: p.throwIndex,
      ...(this.subject ? { subject: [...this.subject] } : {}),
      ...(this.pick ? { pick: this.pick } : {}),
    });
    p.i++;
    p.faces.push(face);
    if (solo) this.pending = null;
    return face;
  }

  d6(role?: string): number { return role ? this.throw2('d6', [role], () => this.d(6)) : this.d(6); }
  d10(): number { return this.d(10); }

  /** A uniform selection is not a physical die from the RWBR kit. */
  select(count: number): number { return this.throw2('dN', ['selection'], () => this.d(count)); }

  sum2d10(): [number, number] {
    return this.throw2('2d10-sum', ['first', 'second'], () => [this.d(10), this.d(10)]);
  }

  /**
   * Two dice read as a SUM, 2-12.
   * Used by: ESCAPE checks, REPAIR rolls, FAST PIT, the DUEL instigator table,
   * the FEUD/FUME results table, and the RACE CAM metallic total.
   */
  sum2d6(): { total: number; dice: [number, number]; doubles: boolean } {
    return this.throw2('2d6-sum', ['first', 'second'], () => {
      const a = this.d(6);
      const b = this.d(6);
      return { total: a + b, dice: [a, b] as [number, number], doubles: a === b };
    });
  }

  /**
   * Two dice read as a TWO-DIGIT code, 11-66 — NOT a sum.
   * Used by: PROBLEM FINDER, ODD PROBLEM, RARE RESULTS, the DRIVER FINDERs,
   * pit violations, and every Race Week chart.
   *
   * Convention, ruled from the DRIVER FINDER tables: the TENS digit is the RED
   * die and the ONES digit is the WHITE die.
   */
  code1166(): { code: string; red: number; white: number; doubles: boolean } {
    return this.throw2('11-66', ['red', 'white'], () => {
      const red = this.d(6);
      const white = this.d(6);
      return { code: `${red}${white}`, red, white, doubles: red === white };
    });
  }

  /**
   * The RACE CAM roll: one black die plus two metallic dice (rulebook p.iii).
   * Black picks the group; the metallic total is read on the track card.
   * Metallic doubles trigger MULTIPLE EVENTS.
   *
   * The three faces are drawn in the same order as before D-018 — black, then
   * the two metallics — so a seeded race is unchanged. It no longer delegates
   * to `sum2d6` only because the metallic dice are METALLIC and the player has
   * to be told which physical die to pick up.
   */
  raceCam(): { black: number; metallic: [number, number]; total: number; doubles: boolean } {
    return this.throw2('race-cam', ['black', 'metallic', 'metallic'], () => {
      const black = this.d(6);
      const a = this.d(6);
      const b = this.d(6);
      return { black, metallic: [a, b] as [number, number], total: a + b, doubles: a === b };
    });
  }

  /**
   * The challenge roll: red, white and blue together (rulebook p.vi-vii).
   * red   vs the challenger's GAS PEDAL
   * white selects the defender in the group above
   * blue  vs the defender's BRAKE PEDAL
   * All three matching is a TRIPLE and voids the challenge.
   */
  challenge(): { red: number; white: number; blue: number; triple: boolean } {
    return this.throw2('challenge', ['red', 'white', 'blue'], () => {
      const red = this.d(6);
      const white = this.d(6);
      const blue = this.d(6);
      return { red, white, blue, triple: red === white && white === blue };
    });
  }

  // -------------------------------------------------------------------------
  // Throw scoping
  // -------------------------------------------------------------------------

  private beginThrow(notation: DrawNotation, roles: readonly string[]): void {
    this.pending = { notation, roles, i: 0, faces: [], throwIndex: this.nextThrow++ };
  }

  /**
   * Run a multi-die throw with its dice named.
   *
   * Inert in seeded mode — it calls straight through — so nothing about the
   * seeded path changes shape. `finally` restores the previous scope rather
   * than clearing it, so a nested throw (none exist today, but the shape should
   * not be a trap) cannot orphan its parent.
   */
  private throw2<T>(notation: DrawNotation, roles: readonly string[], fn: () => T): T {
    if (!this.fed && !this.observe) return fn();
    const saved = this.pending;
    this.beginThrow(notation, roles);
    try {
      return fn();
    } finally {
      this.pending = saved;
    }
  }
}
